import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/providers/AuthProvider';
import { handleAPIError } from '@/utils/errorHandler';
import {
  createFieldCorrection,
  getFieldCorrections,
  updateFieldCorrection,
} from '@/api/fieldCorrections';

export const useFieldCorrections = (ticketId: string) => {
  const { organization } = useAuth();
  const orgId = organization?.id;

  const query = useQuery({
    queryKey: ['fieldCorrections', orgId, ticketId],
    queryFn: () => getFieldCorrections(ticketId),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};

export const useCreateFieldCorrection = () => {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.id;

  const mutation = useMutation({
    mutationFn: (newCorrection: any) => createFieldCorrection(newCorrection),
    // get ticketId from newCorrection to invalidate only that ticket's corrections
    onSuccess: (newCorrection) => {
      queryClient.invalidateQueries({
        queryKey: ['fieldCorrections', orgId, newCorrection.ticketId],
      });
    },
    onError: (error) => {
      handleAPIError(error);
    },
  });

  return {
    createFieldCorrection: mutation.mutate,
    createFieldCorrectionAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
};

export const useUpdateFieldCorrection = () => {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.id;

  const mutation = useMutation({
    mutationFn: (correction: any) => updateFieldCorrection(correction),
    // get ticketId from newCorrection to invalidate only that ticket's corrections
    onSuccess: (newCorrection) => {
      queryClient.invalidateQueries({
        queryKey: ['fieldCorrections', orgId, newCorrection.ticketId],
      });
    },
    onError: (error) => {
      handleAPIError(error);
    },
  });

  return {
    updateFieldCorrection: mutation.mutate,
    updateFieldCorrectionAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
};
